import { Fragment } from 'react';
import { Popover } from '@headlessui/react';
import { useMedia } from 'react-use';
import { useIdentify } from '@hungryroot/customer';
import { Sheet } from '@hungryroot/funnel';
import {
  SpiceNone,
  SpiceMild,
  SpiceMedium,
  SpiceHot,
  CaretDown,
  Check,
} from '@hungryroot/icons';
import { Paragraph, Heading } from '@hungryroot/ramen';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { usePersistSpicePreference } from '../../../../api/update_spicy_level/post/update_spicy_preference';
import { SpicyLevel } from '../../../../components/SpicyButtons/spice-level';
import styles from './ControlSpicyPopover.module.css';

interface Props {
  testid?: string;
}

const spiceOptions = [
  {
    id: SpicyLevel.noHeat,
    title: 'No heat',
    subtitle: 'ex: red bell pepper',
    icon: <SpiceNone />,
  },
  {
    id: SpicyLevel.mild,
    title: 'Mild',
    subtitle: 'ex: pepperoncini pepper',
    icon: <SpiceMild />,
  },
  {
    id: SpicyLevel.medium,
    title: 'Medium',
    subtitle: 'ex: jalapeno pepper',
    icon: <SpiceMedium />,
  },
  {
    id: SpicyLevel.hot,
    title: 'Hot',
    subtitle: 'ex: cayenne pepper',
    icon: <SpiceHot />,
  },
];

function getActiveSpiceLevel(
  active: SpicyLevel | null | undefined,
  testid?: string
) {
  const spiceLevel = spiceOptions.find((item) => item.id === active);
  return (
    <Fragment key="button">
      <div className={styles['button-body']}>
        {spiceLevel ? spiceLevel.icon : null}
        <div>
          {spiceLevel ? (
            <Fragment key="button-text">
              <Heading testid={`${testid}-button-heading`} variant="H5">
                {spiceLevel.title}
              </Heading>
              <Paragraph className={styles.label}>
                {spiceLevel.subtitle}
              </Paragraph>
            </Fragment>
          ) : (
            <Paragraph className={styles['button-text-default']}>
              Select your heat tolerance
            </Paragraph>
          )}
        </div>
      </div>
      <div className={styles['button-icon']}>
        <CaretDown />
      </div>
    </Fragment>
  );
}

export function ControlSpicyPopover(props: Props) {
  const { testid } = props;
  const persistSpicePreferenceMutation = usePersistSpicePreference();
  const isDesktop = useMedia(BreakpointQueryLarge);

  const { customerData } = useIdentify();
  const customerSpicyLevel = customerData?.spicy_level;

  const content = (
    <ul className={styles.list}>
      {spiceOptions.map((item) => {
        return (
          <li
            data-testid={`${testid}-item-${item.id}`}
            key={item.id}
            className={styles.item}
            onClick={() => {
              persistSpicePreferenceMutation.mutate({
                data: { spicy_level: item.id || 0 },
              });
            }}
          >
            <div className={styles['item-body']}>
              {item.icon}
              <div>
                <Heading variant="H5">{item.title}</Heading>
                <Paragraph className={styles.label}>{item.subtitle}</Paragraph>
              </div>
            </div>
            {item.id === customerSpicyLevel ? (
              <div
                data-testid={`${testid}-item-${item.id}-active`}
                className={styles['item-active']}
              >
                <Check />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  const buttonContent = getActiveSpiceLevel(customerSpicyLevel, testid);

  return (
    <Popover>
      {({ open, close }) => {
        return (
          <Fragment key="spicy-popover">
            <Popover.Button
              data-testid={`${testid}-button`}
              className={styles.button}
            >
              {buttonContent}
            </Popover.Button>
            {isDesktop === false ? (
              <Popover.Panel
                as={Sheet}
                testid={`${testid}-panel-mobile`}
                ariaLabel="spicy-popover-sheet"
                onRequestDismiss={() => close()}
                active={open}
                initialFocusRef={undefined}
                static
              >
                {content}
              </Popover.Panel>
            ) : (
              <Popover.Panel
                data-testid={`${testid}-panel-desktop`}
                className={styles.panel}
              >
                {content}
              </Popover.Panel>
            )}
          </Fragment>
        );
      }}
    </Popover>
  );
}
